import { hasConsent } from '@/lib/cookieConsent';
import {
  trackGoogleDemoScheduled,
  trackGooglePageView,
  trackGoogleWhatsAppClick,
} from '@/lib/googleTags';
import { landingApiBase } from '@/lib/api';
import { resolveVisitorContext } from '@/lib/visitorLocale';

const SESSION_KEY = 'landing_session_id';

function getSessionId(): string {
  if (!hasConsent('analytics')) return '';
  try {
    const existing = localStorage.getItem(SESSION_KEY);
    if (existing && existing.length >= 8) return existing;
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return `s-${Date.now()}`;
  }
}

function readClickIds(): { gclid?: string; gbraid?: string; wbraid?: string } {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = {
      gclid: params.get('gclid')?.trim() || undefined,
      gbraid: params.get('gbraid')?.trim() || undefined,
      wbraid: params.get('wbraid')?.trim() || undefined,
    };
    const storedRaw = sessionStorage.getItem('landing_click_ids');
    const stored = storedRaw ? (JSON.parse(storedRaw) as Record<string, string>) : {};
    const merged = {
      gclid: fromUrl.gclid || stored.gclid,
      gbraid: fromUrl.gbraid || stored.gbraid,
      wbraid: fromUrl.wbraid || stored.wbraid,
    };
    if (fromUrl.gclid || fromUrl.gbraid || fromUrl.wbraid) {
      sessionStorage.setItem('landing_click_ids', JSON.stringify(merged));
    }
    return {
      ...(merged.gclid ? { gclid: merged.gclid } : {}),
      ...(merged.gbraid ? { gbraid: merged.gbraid } : {}),
      ...(merged.wbraid ? { wbraid: merged.wbraid } : {}),
    };
  } catch {
    return {};
  }
}

function newLeadClickRef(): string {
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Agrega [it:…] al texto del wa.me para unir el clic de Ads con el hilo. */
export function attachLeadClickToWhatsappUrl(href: string): { href: string; leadClickRef: string } {
  const leadClickRef = newLeadClickRef();
  const marker = `[it:${leadClickRef}]`;
  try {
    const url = new URL(href, window.location.origin);
    const text = url.searchParams.get('text') || 'Hola';
    const next = text.includes('[it:') ? text : `${text}\n${marker}`;
    url.searchParams.set('text', next);
    return { href: url.toString(), leadClickRef };
  } catch {
    return { href, leadClickRef };
  }
}

function readUtm(): { utmSource?: string; utmMedium?: string; utmCampaign?: string } {
  try {
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get('utm_source')?.trim();
    const utmMedium = params.get('utm_medium')?.trim();
    const utmCampaign = params.get('utm_campaign')?.trim();
    return {
      ...(utmSource ? { utmSource } : {}),
      ...(utmMedium ? { utmMedium } : {}),
      ...(utmCampaign ? { utmCampaign } : {}),
    };
  } catch {
    return {};
  }
}

function readGeo(): { countryIso: string; timeZone: string; countrySource: string } {
  const ctx = resolveVisitorContext();
  return {
    countryIso: ctx.countryIso,
    timeZone: ctx.scheduleTimeZone,
    countrySource: ctx.countrySource,
  };
}

function postLandingPayload(path: string, body: Record<string, unknown>): void {
  const base = landingApiBase();
  if (!base || !hasConsent('analytics')) return;

  const sessionId = getSessionId();
  if (!sessionId) return;

  const payload = JSON.stringify({ sessionId, ...readGeo(), ...body });
  void fetch(`${base}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: payload,
    keepalive: true,
    credentials: 'omit',
    mode: 'cors',
  }).catch(() => {});
}

/**
 * Registra una visita a la landing (deduplicada en servidor ~5 min).
 */
export function trackLandingPageView(path = window.location.pathname): void {
  if (!hasConsent('analytics')) return;
  trackGooglePageView(path);
  postLandingPayload('/pageview', {
    path,
    referrer: document.referrer || undefined,
    ...readUtm(),
  });
}

export type LandingEventProps = Record<string, string | number | boolean | undefined>;

export function trackLandingEvent(eventName: string, properties: LandingEventProps = {}): void {
  if (!hasConsent('analytics')) return;
  const geo = readGeo();
  postLandingPayload('/event', {
    eventName,
    path: window.location.pathname,
    properties: { ...properties, countryIso: geo.countryIso, timeZone: geo.timeZone },
    ...readUtm(),
    ...readClickIds(),
  });
}

export function trackCtaClick(location: string, text: string, href: string): void {
  trackLandingEvent('cta_click', { location, text, href });
}

/** CTA a WhatsApp de ventas: evento interno + conversión Ads/GA4. */
export function trackWhatsAppSalesClick(location: string, text: string, href: string): string {
  const { href: nextHref, leadClickRef } = attachLeadClickToWhatsappUrl(href);
  trackLandingEvent('cta_click', { location, text, href: nextHref, leadClickRef });
  postLandingPayload('/event', {
    eventName: 'whatsapp_click',
    path: window.location.pathname,
    leadClickRef,
    ...readUtm(),
    ...readClickIds(),
    properties: { location, text, href: nextHref, leadClickRef },
  });
  trackGoogleWhatsAppClick(location);
  return nextHref;
}

export function onWhatsAppAnchorClick(
  location: string,
  text: string,
): (e: { currentTarget: { href: string } }) => void {
  return (e) => {
    e.currentTarget.href = trackWhatsAppSalesClick(location, text, e.currentTarget.href);
  };
}

export function trackFormStep(step: string, funnel = 'demo_request'): void {
  trackLandingEvent('form_step', { step, funnel });
}

export function trackFormComplete(): void {
  if (!hasConsent('analytics')) return;
  trackGoogleDemoScheduled();
  trackLandingEvent('form_complete', { funnel: 'demo_request' });
}

export function trackScrollDepth(percent: number): void {
  trackLandingEvent('scroll_depth', { percent });
}

export function trackSectionView(section: string): void {
  trackLandingEvent('section_view', { section });
}

export function trackDemoWizardOpen(): void {
  trackLandingEvent('demo_wizard_open', {});
}

export function trackExitIntentShown(): void {
  trackLandingEvent('exit_intent_shown', {});
}
